import loadScript from "load-script";

import { newrelic } from "@jti/fe-clients";
import { isServerSide } from "@jti/fe-common";
import { PgsApi } from "@jti/rest-api";

import {
  getPgsClientConstructor,
  getSecuredFieldsSessionConstructor,
  PaymentGatewayError,
} from "../paymentGateway/paymentGateway";

export const scriptTagExists = (src: string): boolean => {
  return !!document.querySelector(`script[src="${src}"]`);
};

export const isPaymentAdapterReady = (): boolean => {
  if (isServerSide()) return false;
  return !!window.__PGSClient;
};

export const loadAdapterScript = async (src: string): Promise<void> => {
  if (isServerSide()) return;

  if (scriptTagExists(src) && isPaymentAdapterReady()) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    loadScript(src, (error: unknown) => {
      if (error) {
        const gatewayError = new PaymentGatewayError("PGS client script failed to load", { cause: error });
        newrelic.noticeError(gatewayError);
        reject(gatewayError);
        return;
      }
      resolve();
    });
  });

  if (!isPaymentAdapterReady()) {
    const gatewayError = new PaymentGatewayError("PGS client global constructor not found after script load");
    newrelic.noticeError(gatewayError);
    throw gatewayError;
  }
};

export const getPaymentAdapterScript = async (paymentMethodCode: string): Promise<string> => {
  try {
    const pgsApi = PgsApi.init();
    const clientScriptUrl = await pgsApi.getClient(paymentMethodCode);

    if (!clientScriptUrl) {
      throw new PaymentGatewayError("Empty client script url returned by PGS API");
    }

    return clientScriptUrl;
  } catch (error) {
    if (error instanceof PaymentGatewayError) {
      newrelic.noticeError(error);
      throw error;
    }
    const gatewayError = new PaymentGatewayError("Failed to resolve payment adapter script", { cause: error });
    newrelic.noticeError(gatewayError);
    throw gatewayError;
  }
};

export const getAdapterSupportedFlows = (paymentMethodCode: string): string[] => {
  const PGSClient = getPgsClientConstructor();

  if (!PGSClient || typeof PGSClient.getSupportedPaymentFlows !== "function") {
    return ["HOSTED_CHECKOUT"];
  }

  return PGSClient.getSupportedPaymentFlows(paymentMethodCode);
};

export const checkAdapterSupportsSecuredFields = (paymentMethodCode: string): boolean => {
  const securedFieldsSessionApi = getSecuredFieldsSessionConstructor()?.prototype;

  if (!securedFieldsSessionApi) {
    return false;
  }

  return (
    getAdapterSupportedFlows(paymentMethodCode).includes("SECURED_FIELDS") &&
    typeof securedFieldsSessionApi.loadSdk === "function" &&
    typeof securedFieldsSessionApi.configure === "function" &&
    typeof securedFieldsSessionApi.validate === "function"
  );
};
